(ns app.front.view.fee.list
  (:require ["react-transition-group/esm/CSSTransition" :default CSSTransition]
            ["react-transition-group/esm/TransitionGroup" :default TransitionGroup]
            [app.domain.business.model :as business-model]
            [app.domain.fee.escrow-model :as escrow-model]
            [app.domain.fee.model :as fee-model]
            [app.domain.group.model :as group-model]
            [app.domain.party.model :as party-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as table]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.application.permissions :as permissions]
            [app.front.view.fee.bond :as fee-bond]
            [app.front.view.fee.events :as fee-events]
            [app.front.view.fee.set-paid-modal :as set-paid-modal]
            [app.front.view.fee.subs :as fee-subs]
            [app.front.view.party.subs :as party-subs]
            [app.front.view.payment.checkout :as checkout]
            [app.front.view.payment.forte]
            [app.front.view.payment.heartland]
            [app.front.view.payment.moneris-co]
            [app.front.view.payment.paypal]
            [app.front.view.project.bill-events :as bill-events]
            [app.front.view.project.bill-subs :as bill-subs]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def bill-paid-or-cancelled? (du/in-state-fn :bill.state/paid
                                             :bill.state/canceled))

(defn show-bills-for-phase-entity? [phase-entity]
  (or ;; for draft apps, don't show the deposit bill yet
    (not (fee-model/hide-bills-from-applicant? phase-entity))
    ;; unless user is authority
    (-> phase-entity
        :granted-permissions
        (contains? :permission.application/review-payments))))

(rf/reg-sub ::bills
  (fn [[_ phase-id]]
    (rf/subscribe [::entities/entities {:entity/type   :entity.type/bill
                                        :entity/parent phase-id}]))
  (fn [bills _]
    (->> bills
         (sort-by :entity/created >))))


(rf/reg-sub ::bills-visible-in-phase
  (fn [[_ phase]]
    (rf/subscribe [::bills (:db/id phase)]))
  (fn [bills [_ phase]]
    (cond->> bills
      (not (show-bills-for-phase-entity? phase)) (filter (du/in-state-fn :bill.state/paid
                                                                         :bill.state/pending-confirmation)))))


(rf/reg-sub ::invoices-paid?
  (fn [[_ phase-id]]
    (rf/subscribe [::bills phase-id]))
  (fn [bills _]
    (let [has-invoices      (> (count bills) 0)
          all-invoices-paid (every? bill-paid-or-cancelled? bills)]
      (and has-invoices all-invoices-paid))))


(rf/reg-event-fx ::fetch-fee-schedules
  (fn [_ [_ workspace-id schedules-in-drafts]]
    {:comm/command {:command    :fee/published-schedules
                    :method     :get
                    :data       {:workspace-id workspace-id}
                    :on-success [::fetch-fee-schedules.success workspace-id schedules-in-drafts]}}))


(rf/reg-event-fx ::fetch-fee-schedules.success
  (fn [_ [_ workspace-id schedules-in-drafts status schedules]]
    (let [schedules-to-fetch (-> schedules-in-drafts
                                 (conj (-> schedules last :db/id))
                                 distinct)]
      {:fx (concat [[:dispatch [::entities/merge-entity-db status (du/entities->entity-db schedules)]]]
                   (map (fn [sched]
                          [:dispatch [::fetch-fee-schedule-data workspace-id sched]])
                        schedules-to-fetch))})))


(rf/reg-event-fx ::fetch-fee-schedule-data
  (fn [_ [_ workspace-id schedule-id]]
    (when schedule-id
      {:comm/command {:command    (str "fee/schedule/" schedule-id)
                      :data       {:workspace-id workspace-id}
                      :method     :get
                      :on-success [::entities/merge-entity-db]}})))


(rf/reg-sub ::fee-schedules
  :<- [::ws-subs/authority-group]
  (fn [auth-group _]
    (->> @(rf/subscribe [::entities/entities {:entity/type   :entity.type/fee-schedule
                                              :entity/parent (:db/id auth-group)}])
         (filter fee-model/valid-schedule?)
         (remove :fee-schedule/archived?))))


(rf/reg-sub ::fee-templates
  (fn [[_ schedule-id]]
    [(rf/subscribe [::entities/entity schedule-id])
     (rf/subscribe [:language])])
  (fn [[schedule lang] _]
    (->> schedule
         :entity/_parent
         (filter #(eu/is-type? :entity.type/fee-template %))
         (sort-by #(fee-model/fee-template-title % lang)))))


(rf/reg-sub ::payer-required?
  :<- [::ws-subs/has-feature? :generate-bill-pdf?]
  (fn [enabled?]
    enabled?))


(rf/reg-event-fx ::update-bill-invoice-number
  (fn [_ [_ fsm-target bill-id invoice-number]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/update-bill-invoice-number {:bill-id        bill-id
                                                             :invoice-number invoice-number}]
                {:notify {:success :fees/bill-updated}}]}))

(rf/reg-event-fx ::update-bill-payer
  (fn [_ [_ fsm-target bill-id payer-id]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/update-bill-payer {:bill-id  bill-id
                                                    :payer-id payer-id}]
                {:notify {:success :fees/bill-updated}}]}))

(rf/reg-event-fx ::update-bill-due-date
  (fn [_ [_ fsm-target bill-id due-date-ts]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/update-bill-due-date {:bill-id     bill-id
                                                       :due-date-ts due-date-ts}]
                {:notify {:success :fees/bill-updated}}]}))

(rf/reg-event-fx ::update-bill-fee-schedule
  (fn [_ [_ fsm-target bill-id fee-schedule-id]]
    {:fx [[:dispatch [::process/signal
                      fsm-target
                      [:process.signal/update-bill-fee-schedule {:bill-id         bill-id
                                                                 :fee-schedule-id fee-schedule-id}]
                      {:notify {:success :fees/bill-updated}}]]
          [:dispatch [::fetch-fee-schedule-data
                      (-> fsm-target :entity/parent :workspace/id)
                      fee-schedule-id]]]}))


(rf/reg-event-fx ::update-fee-text-attr
  (fn [_ [_ fsm-target bill-id fee-id attr value]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/update-draft-fee-attr {:bill-id bill-id
                                                        :fee-id  fee-id
                                                        attr     value}]
                {:notify {:success :fees/fees-updated}}]}))


(rf/reg-event-fx ::update-fee-amount
  (fn [_ [_ fsm-target entity-version bill-id fee-id field value]]
    {:dispatch [::process/signal fsm-target
                [:process.signal/update-amount {:version entity-version
                                                :bill-id bill-id
                                                :fee-id  fee-id
                                                field    value}]
                {:notify {:success :fees/fees-updated}}]}))


(rf/reg-event-fx ::initiate-online-payment
  (fn [{:keys [db]} [_ bill-id opts]]
    (let [{:keys [return-path return-route]} (or opts (::checkout/default-return-opts db))]
      {:db           (cond-> (assoc db ::pending-payment bill-id)
                       return-route (assoc ::checkout/payment-return-route return-route))
       :comm/command {:command        :payment/get-form
                      :data           (p/assoc-if {:bill-id bill-id} :return-path return-path)
                      :on-success     [::send-or-render-payment-form]
                      :on-bad-request [::fetch-payment-form.error]
                      :on-done        [::payment-action.done]}})))


(rf/reg-event-fx ::partial-submit-and-initiate-online-payment
  (fn [{:keys [db]} [_ fsm-target bill-id opts]]
    {:db       (assoc db ::pending-payment bill-id)
     :dispatch [::process/signal
                fsm-target
                [:process.signal/partial-submit]
                {:on-success [::initiate-online-payment bill-id opts]}]}))


(rf/reg-event-fx ::fetch-payment-form.error
  (fn [{:keys [db]} [_ _status body request-id]]
    {:db       (dissoc db ::pending-payment)
     :dispatch [:notifications/add
                :toast
                :error
                :error/error
                (or (:error body) :error-with-req-id/unknown)
                request-id]}))


(rf/reg-event-db ::payment-action.done
  (fn [db _]
    (dissoc db ::pending-payment)))


(rf/reg-event-fx ::send-or-render-payment-form
  (fn [{:keys [db]} [_ _ {:keys [form-html transaction-details]}]]
    (if form-html
      {::fee-events/submit-payment-form form-html}
      (merge {:db (-> (assoc db ::checkout/transaction-details transaction-details)
                      (dissoc ::pending-payment))}
             (if (and (ff/enabled? :feature-flag.id/checkout-page-v2)
                      (-> transaction-details :ctx :safe-js? not))
               {:navigate-to (href/href :view/payment-checkout-v2
                                        {:workspace-id (-> db :view/workspace :workspace/id)
                                         :payment-id   (-> transaction-details :ctx :payment-id)})}
               {:route/push {:route  :view/payment-checkout
                             :params {:workspace-id (-> db :view/workspace :workspace/id)}}
                :fx         (checkout/payment-service-effects transaction-details)})))))


(rf/reg-event-fx ::pay-with-escrow
  (fn [{:keys [db]} [_ fsm-target bill-id account-id]]
    {:db       (assoc db ::pending-payment bill-id)
     :dispatch [::process/signal
                fsm-target
                [:process.signal/pay-with-escrow (util/mmap bill-id account-id)]
                {:notify  {:success :payment/success}
                 :on-done [::payment-action.done]}]}))


(rf/reg-event-fx ::partial-submit-and-pay-with-escrow
  (fn [{:keys [db]} [_ fsm-target bill-id account-id]]
    {:db       (assoc db ::pending-payment bill-id)
     :dispatch [::process/signal
                fsm-target
                [:process.signal/partial-submit]
                {:on-success [::pay-with-escrow fsm-target bill-id account-id]
                 :on-done    [::payment-action.done]}]}))


(rf/reg-event-fx ::add-fee
  (fn [_ [_ fsm-target bill-id fee-template-id]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/add-fee {:bill-id         bill-id
                                          :fee-template-id fee-template-id}]]}))


(rf/reg-event-fx ::remove-fee
  (fn [_ [_ fsm-target bill-id row-id]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/remove-fee {:bill-id bill-id
                                             :fee-id  row-id}]]}))


(rf/reg-event-db ::set-bill-cancel-reason
  (fn [db [_ key data]]
    (assoc-in db [:bill key :cancel-reason] data)))


(rf/reg-sub ::bill-cancel-reason
  (fn [db [_ bill-id]]
    (get-in db [:bill bill-id :cancel-reason])))


(rf/reg-sub ::bill-total
  (fn [[_ bill-id] _]
    (rf/subscribe [::entities/entity bill-id]))
  fee-model/bill-amount)


(rf/reg-sub ::unpaid-total
  (fn [[_ fsm-target-id] _]
    (rf/subscribe [::bills fsm-target-id]))
  (fn [bills _]
    (->> bills
         (filter #(= (-> % :domain/state) :bill.state/unpaid))
         (mapcat :bill/fees)
         (map :fee/amount)
         (reduce +))))


(rf/reg-sub ::bill-discounts
  (fn [_ _]
    0))


(rf/reg-sub ::fees-section-visible?
  (fn [[_ fsm-target] _]
    [(rf/subscribe [::bills-visible-in-phase fsm-target])
     (rf/subscribe [::process/signal-permitted? fsm-target [:process.signal/cancel-bill]])
     (rf/subscribe [::process/signal-permitted? fsm-target [:process.signal/add-bill {:phase :bill.phase/permit}]])
     (rf/subscribe [::process/signal-permitted? fsm-target [:process.signal/add-bill {:phase :bill.phase/additional}]])
     (rf/subscribe [::process/signal-permitted?
                    fsm-target
                    [:process.signal/add-pre-consultation-bill {:phase :bill.phase/pre-consultation}]])
     (rf/subscribe [::ff/enabled? :feature-flag.id/inspection-ui-improvements])])
  (fn [[bills cancel-permitted? add-permit-bill? add-additional-bill? add-pre-con-bill? ui-improvements?] _]
    (if ui-improvements?
      (seq bills)
      (or (seq bills)
          cancel-permitted?
          add-permit-bill?
          add-additional-bill?
          add-pre-con-bill?))))


(rf/reg-sub ::pending-payment :-> ::pending-payment)


(rf/reg-sub ::escrow-accounts
  (fn [[_ fsm-target domain-group-id] _]
    [(rf/subscribe [::entities/entities {:entity/type   :entity.type/escrow-account
                                         :domain/state  :escrow-account.state/active
                                         :entity/parent domain-group-id}])
     (rf/subscribe [::party-subs/parties-for-current-user fsm-target])])
  (fn [[accounts parties] _]
    (when (seq accounts)
      (let [party-user-ids (->> parties
                                (keep #(-> (or (:party/user %)
                                               (:party/business %))
                                           :db/id))
                                set)]
        (->> accounts
             (filter (fn [account]
                       (and (contains? party-user-ids
                                       (-> account :escrow-account/owner :db/id))
                            (contains? (:granted-permissions account)
                                       :permission.escrow/use-for-transactions))))
             seq)))))


(rf/reg-sub ::show-escrow-option?
  (fn [[_ fsm-target bill-id] _]
    (rf/subscribe [::process/signal-errors
                   fsm-target
                   [:process.signal/pay-with-escrow
                    (util/mmap bill-id)]]))
  (fn [errors _]
    (set/subset? (set errors)
                 #{:process.guard/escrow-account-in-domain-group?
                   :process.guard/sufficient-escrow-balance?})))


(rf/reg-sub ::valid-escrow-account?
  (fn [[_ fsm-target bill-id account-id] _]
    (rf/subscribe [::process/signal-permitted?
                   fsm-target
                   [:process.signal/pay-with-escrow
                    (util/mmap bill-id account-id)]]))
  (fn [valid? _]
    valid?))


(rf/reg-sub ::escrow-modal-open?
  (fn [db [_ bill-id]]
    (= (:escrow-modal-open db) bill-id)))


(rf/reg-event-db ::open-escrow-modal
  (fn [db [_ bill-id]]
    (assoc db :escrow-modal-open bill-id)))


(rf/reg-event-db ::close-escrow-modal
  (fn [db _]
    (dissoc db :escrow-modal-open)))


(rf/reg-event-db ::set-selected-escrow-account
  (fn [db [_ account]]
    (assoc db :selected-escrow-account account)))


(rf/reg-sub ::selected-escrow-account
  :-> :selected-escrow-account)

;; Pending fee changes state management
(rf/reg-event-db ::toggle-pending-fee-change
  (fn [db [_ bill-id fee-id operation-type parent-fee-id]]
    (update-in db [::pending-fee-changes bill-id]
               (fn [changes]
                 (let [current-changes (or changes {})]
                   (if (= operation-type (get-in current-changes [fee-id :operation]))
                     ;; Same operation - toggle off (remove pending change)
                     (dissoc current-changes fee-id)
                     ;; Different operation or new - set pending change
                     (assoc current-changes fee-id
                            {:operation     operation-type
                             :parent-fee-id parent-fee-id})))))))

(rf/reg-event-db ::clear-pending-changes
  (fn [db [_ bill-id]]
    (update db ::pending-fee-changes dissoc bill-id)))

(rf/reg-event-fx ::apply-fee-changes
  (fn [{:keys [db]} [_ fsm-target bill-id]]
    (let [pending-changes (get-in db [::pending-fee-changes bill-id])
          operations      (mapv (fn [[fee-id {:keys [operation parent-fee-id]}]]
                                  {:fee-id        fee-id
                                   :operation     operation
                                   :parent-fee-id parent-fee-id})
                                pending-changes)]
      {:db       (update db ::pending-fee-changes dissoc bill-id)
       :dispatch [::process/signal
                  fsm-target
                  [:process.signal/batch-waive-restore-fees
                   {:bill-id    bill-id
                    :operations operations}]
                  {:notify {:success :fees/changes-saved}}]})))

(rf/reg-sub ::pending-fee-changes
  (fn [db [_ bill-id]]
    (get-in db [::pending-fee-changes bill-id] {})))

(defn signal-permitted? [fsm-target signal]
  (<sub [::process/signal-permitted? fsm-target signal]))


(defn valid-number-string? [val]
  (re-matches fee-model/fee-number-string-pattern (str val)))

(defn- fee-number-input [{:keys [id default-value aria-label]} change-fn]
  (r/with-let [*value (r/atom nil)]
    (let [value    (or @*value default-value)
          invalid? (not (valid-number-string? value))]
      [:input.bx--text-input
       {:id           id
        :class        "bg-white"
        :disabled     (<sub [::process/signal-processing? :process.signal/update-amount])
        :value        value
        :data-invalid (when invalid? "true")
        :aria-invalid (when invalid? "true")
        :aria-label   aria-label
        :on-input     (fn [e]
                        (let [new-val (str/replace (futil/tval e) "," "")]
                          (when (not= new-val value)
                            (reset! *value new-val))))
        :on-blur      (fn [e]
                        (let [value (-> e futil/tval)]
                          (when (and (valid-number-string? value)
                                     ;; only update if value was actually changed
                                     (some? @*value)
                                     (not= default-value value))
                            (change-fn value))))}])))


(def minimum-fee-indicator
  [:span.px-2 "**"])


(defn is-minimum-amount?
  "Returns true if the fee amount has been overridden by the minimum amount for the fee"
  [row]
  (and (pos? (double (:fee/precise-unit-price row)))
       (<= (:fee/amount row) (:fee-template/minimum-total row))))


(defn fee-item-flag [tr attr row]
  (when (attr row)
    [:div.text-xs.mt-1.lg:ml-1 (tr [attr])]))

(defn flags [tr row]
  [:<>
   [fee-item-flag tr :fee-template/allow-including-in-property-tax? row]
   [fee-item-flag tr :fee-template/refundable? row]])


(defn group-bill-rows [lang tr rows]
  (->> rows
       (sort-by #(fee-model/fee-title % lang tr))
       (group-by #(-> % :fee-template/group (fee-model/fee-group-title lang)))
       (sort-by first)
       (map (fn [[title rows]]
              (let [rows (fee-model/bill-rows-with-penalties-and-interests rows)]
                (util/mmap {:subtotal (->> rows
                                           (keep :fee/amount)
                                           (reduce +))}
                           title rows))))))

(defn group-header [group-title]
  [{:opts    {:class "mr-4 font-bold pt-3"}
    :content group-title}])

(defn subtotal-row [{:keys [tr title subtotal currency draft?]}]
  [{:opts    {:class "mr-4 font-bold pt-3"}
    :content ""}
   {:opts    {:class ["text-right pt-3"
                      (when draft? "mr-10")]}
    :content [:div
              (tr [:fees/subtotal] [title])
              [:span.font-mono (futil/money-string subtotal currency)]]}])

(defn title-and-subtotal [{:keys [tr currency title subtotal draft?]} rows]
  (concat (when title
            [{:key         (str "row-" title)
              :opts        {:class "pb-0"}
              :sub-header? true
              :cols        (group-header title)}])
          rows
          (when title
            [{:key         (str "row-" title "-" subtotal)
              :opts        {:class "pb-0"}
              :sub-header? true
              :cols        (subtotal-row (util/mmap tr title subtotal currency draft?))}])))

(defn date-multiplier [{:keys                        [footnotes]
                        {:fee/keys [date-multiplier]
                         :as       fee}              :fee}]
  (when date-multiplier
    [:span.ml-2.text-purple-800
     (fee-model/footnote-reference-text footnotes fee)]))

(defn- render-draft-bill-rows [{:keys [fsm-target bill-id rows tr currency footnotes]}]
  (let [lang     (<sub [:language])
        show-wo? (some :fee-template/work-order-number? rows)]
    #_{:clj-kondo/ignore [:deprecated-var]}
    [table/responsive-table {:tr                    tr
                             :class                 "draft-bill-rows mb-4"
                             :row-class-override    "lg:items-center px-4 py-2"
                             :expandable?           false
                             :table-mode-breakpoint (if show-wo? :xl :lg)}
     [{:title (tr [:fees/item])
       :width "250px"}
      {:title (tr [:fees/notes])}
      (when show-wo?
        {:title (tr [:fee/work-order-number])
         :width "180px"})
      {:title (tr [:fees/quantity])
       :width "120px"}
      {:title (tr [:fees/unit])
       :width "45px"}
      {:title (tr [:fees/unit-price])
       :width "120px"}
      {:title (tr [:fees/item-total])
       :width "120px"
       :class "text-right"}
      {:title ""
       :width "40px"}]
     (->> rows
          (group-bill-rows lang tr)
          (mapcat (fn [{:keys [title rows subtotal]}]
                    (->> rows
                         (mapv (fn [{:db/keys [id] :as fee}]
                                 (let [item-type       (fee-model/fee-title fee lang tr)
                                       entity-version  (:entity/version fee)
                                       quantity        (:fee/precise-quantity fee)
                                       unit            (:fee-template/unit fee)
                                       unit-price      (:fee/precise-unit-price fee)
                                       row-total       (or (-> fee :fee/amount) 0)
                                       negative?       (< row-total 0)
                                       discount?       false
                                       late-fee?       (fee-model/late-fee-types (:fee/type fee))
                                       fee-description (if late-fee?
                                                         (fee-model/late-fee-description fee tr currency (<sub [::date-util/date-fn]))
                                                         (-> fee :fee/description))
                                       update-attr-fn  (fn [attr]
                                                         #(when (futil/value-changed? %)
                                                            (>evt [::update-fee-text-attr
                                                                   fsm-target
                                                                   bill-id
                                                                   id
                                                                   attr
                                                                   (-> % futil/tval)])))]
                                   {:key  (str "row-" id)
                                    :opts {:class "pb-0"}
                                    :cols [{:opts    {:class "pt-3"}
                                            :content [:<>
                                                      item-type
                                                      [flags tr fee]
                                                      [:div.mt-2
                                                       [date-multiplier (util/mmap fee footnotes)]]]}
                                           {:opts    {}
                                            :content [ui/text-input {:id            (str "input-notes-" bill-id "-" id)
                                                                     :label-text    (tr [:fees/notes])
                                                                     :hide-label    true
                                                                     :light         true
                                                                     :default-value fee-description
                                                                     :on-blur       (update-attr-fn :fee/description)}]}
                                           (when show-wo?
                                             {:opts    {}
                                              :content (when (:fee-template/work-order-number? fee)
                                                         [ui/text-input
                                                          {:id            (str "input-work-order-" bill-id "-" id)
                                                           :label-text    (tr [:fee/work-order-number])
                                                           :hide-label    true
                                                           :light         true
                                                           :default-value (:fee/work-order-number fee)
                                                           ;; This is based on the character limit in Tempest Prospero
                                                           ;; See WIN-2657
                                                           ;; At the time of writing there are no other users for work
                                                           ;; order numbers, but this might have to be configurable
                                                           ;; in the future.
                                                           :max-length    10
                                                           :on-blur       (update-attr-fn :fee/work-order-number)}])})
                                           {:opts    {}
                                            :content [fee-number-input
                                                      {:id            (str "input-quantity-" bill-id "-" id)
                                                       :default-value quantity
                                                       :aria-label    (tr [:fees/quantity])}
                                                      #(>evt [::update-fee-amount
                                                              fsm-target
                                                              entity-version
                                                              bill-id
                                                              id
                                                              :quantity
                                                              %])]}
                                           {:opts    {:class "pt-3"}
                                            :content unit}
                                           {:opts    {}
                                            :content [fee-number-input
                                                      {:id            (str "input-unit-price-" bill-id "-" id)
                                                       :default-value unit-price
                                                       :aria-label    (tr [:fees/unit-price])}
                                                      #(>evt [::update-fee-amount
                                                              fsm-target
                                                              entity-version
                                                              bill-id
                                                              id
                                                              :unit-price
                                                              %])]}
                                           {:opts    {:class (futil/cs "lg:text-right pt-3"
                                                                       (when negative? "text-red-700"))}
                                            :content [:div.flex.items-center
                                                      {:class [(if discount?
                                                                 "justify-between"
                                                                 "justify-end")]}
                                                      (if (<sub [::process/signal-processing? :process.signal/update-amount])
                                                        (cc/loading-spinner
                                                          {:small        true
                                                           :with-overlay false
                                                           :class-name   "inline-block"})
                                                        [:<>
                                                         (when discount?
                                                           [:div.mr-2.text-red-600.border-2.border-solid.border-red-600.rounded-full.font-bold.text-xs
                                                            {:style {:padding "4px 5px"}}
                                                            "%"])
                                                         (when (is-minimum-amount? fee)
                                                           minimum-fee-indicator)
                                                         [:span.font-mono (futil/money-string row-total currency)]])]}
                                           {:opts    {:class "text-right pt-2"}
                                            :content [ui/modal-confirmation-button
                                                      {:tr                tr
                                                       :id                "remove-fee"
                                                       :trigger-icon-opts {:icon        i/trash
                                                                           :description (tr [:remove])
                                                                           :class-name  "cursor-pointer"
                                                                           :fill        "gray"}
                                                       :modal-title       (tr [:fees/remove-fee])
                                                       :modal-class       "text-left"
                                                       :on-yes-click      #(>evt [::remove-fee fsm-target bill-id id])}
                                                      (tr [:fees/remove-fee-information])]}]})))
                         (title-and-subtotal (util/mmap {:draft? true} tr currency title subtotal))))))]))

(defn- get-fee-waive-state
  "Returns {:checked boolean :title string :operation keyword} for a fee"
  [row fee-waived? pending-changes tr]
  (let [fee-id     (:db/id row)
        pending-op (get-in pending-changes [fee-id :operation])]
    (case pending-op
      :waive   {:checked true :title (tr [:fees/pending-waive]) :operation :waive}
      :restore {:checked false :title (tr [:fees/pending-restore]) :operation :restore}
      ;; No pending change - use actual state
      (if fee-waived?
        {:checked true :title (tr [:fees/restore-fee]) :operation :restore}
        {:checked false :title (tr [:fees/waive-fee]) :operation :waive}))))

(defn- render-waive-restore-checkbox
  [row table-bp id fee-waived? can-waive-and-restore? tr bill-id]
  (when (and (fee-model/late-fee-types (:fee/type row))
             (#{:fee.state/unpaid :fee.state/waived} (:domain/state row)))
    (let [pending-changes                   (<sub [::pending-fee-changes bill-id])
          {:keys [checked title operation]} (get-fee-waive-state row fee-waived? pending-changes tr)]
      [:div.flex.items-start.justify-center.-mt-2
       (cc/checkbox
         {:id                 (str "waive-checkbox-" id)
          :disabled           (not (can-waive-and-restore? row))
          :wrapper-class-name (str (name table-bp) ":items-center")
          :label-text         ""
          :hide-label         true
          :checked            checked
          :title              title
          :on-change          #(>evt [::toggle-pending-fee-change
                                      bill-id
                                      (:db/id row)
                                      operation
                                      (:db/id (:fee/original-fee row))])})])))

(defn- render-bond-column
  "Renders the bond-related action column."
  [row can-bond? can-cancel-bond? tr id fsm-target bill-id currency]
  (cond
    (can-bond? row)
    [fee-bond/bond-confirm-modal
     {:tr       tr
      :id       (str "use-bond-fee-" id)
      :phase    fsm-target
      :bill-id  bill-id
      :fee      row
      :currency currency}]

    (can-cancel-bond? row)
    [ui/modal-confirmation-button
     {:tr                  tr
      :id                  (str "cancel-bond-use-" id)
      :trigger-button-text (tr [:fees/cancel-bond-use])
      :trigger-button-opts {:kind       :tertiary
                            :size       :small
                            :class-name "px-3"}
      :modal-title         (tr [:fees/cancel-bond-use])
      :modal-class         "text-left"
      :on-yes-click        #(>evt [::process/signal
                                   fsm-target
                                   [:process.signal/cancel-bond-use-for-fee
                                    {:bill-id bill-id
                                     :fee-id  id}]
                                   {:notify {:success :fees/cancel-bond-use-success}}])}
     (tr [:fees/cancel-bond-use-confirm]
         [(futil/money-string (:fee/bond-amount row) currency)])]))

(defn- render-bill-cols [{:keys [row fsm-target tr lang
                                 bill-id currency table-bp bond-used?
                                 can-cancel-bond? show-wo? show-bond-button? can-bond?
                                 footnotes id]}]
  (let [can-waive-and-restore? #(and (signal-permitted? fsm-target [:process.signal/batch-waive-restore-fees
                                                                    {:bill-id bill-id}])
                                     ;; Also check if the fee can actually be waived/restored
                                     (or (fee-model/can-waive-fee? %)
                                         (fee-model/can-restore-fee? %)))]
    (if (vector? row)
      (group-header row)
      (let [deposit?             false
            row-total            (:fee/amount row)
            quantity             (:fee/precise-quantity row)
            unit                 (:fee-template/unit row)
            unit-price           (:fee/precise-unit-price row)
            title                (fee-model/fee-title row lang tr)
            negative-total?      (< row-total 0)
            negative-unit-price? (< unit-price 0)
            late-fee?            (fee-model/late-fee-types (:fee/type row))
            fee-waived?          (= :fee.state/waived (<sub [::entities/entity-attr (:db/id row) :domain/state]))]
        [{:opts    {:class (futil/cs (when (-> row :fee-template/group some?)
                                       (str (name table-bp) ":pl-4"))
                                     (when fee-waived? "bx--label--disabled"))}
          :content [:div {:class (when late-fee? "ml-6")}
                    [:<>
                     title
                     [flags tr row]]]}
         {:opts    {:class (when fee-waived? "bx--label--disabled")}
          :content [:span (if late-fee?
                            (fee-model/late-fee-description row tr currency (<sub [::date-util/date-fn]))
                            (-> row :fee/description))]}
         (when show-wo?
           {:opts    {}
            :content (:fee/work-order-number row)})
         (when show-bond-button?
           {:opts    {}
            :content (render-bond-column row can-bond? can-cancel-bond? tr id fsm-target bill-id currency)})
         {:opts    {:class (when fee-waived? "bx--label--disabled")}
          :content [:span {:class "break-all"}
                    (when-not deposit? quantity)
                    [date-multiplier (util/mmap {:fee row} footnotes)]]}
         {:opts    {:class (when fee-waived? "bx--label--disabled")}
          :content (when-not deposit? unit)}
         {:opts    {:class (futil/cs "font-mono lg:text-right"
                                     (when negative-unit-price? "text-red-700")
                                     (when fee-waived? "bx--label--disabled"))}
          :content [:span {:class "break-all"} unit-price]}
         (when bond-used?
           {:opts    {:class "font-mono lg:text-right"}
            :content (if-let [bond-amount (:fee/bond-amount row)]
                       [:<>
                        (when (is-minimum-amount? row)
                          minimum-fee-indicator)
                        (futil/money-string bond-amount currency)]
                       "")})
         {:content (render-waive-restore-checkbox row table-bp id fee-waived? can-waive-and-restore? tr bill-id)}
         {:opts    {:class (futil/cs "font-mono lg:text-right pr-0"
                                     (when negative-total? "text-red-700")
                                     (when fee-waived? "bx--label--disabled"))}
          :content [:<>
                    (when (is-minimum-amount? row)
                      minimum-fee-indicator)
                    (futil/money-string row-total currency)]}]))))

(defn- render-bill-rows [{:keys [rows tr currency fsm-target bill-id footnotes tz] :as _params}]
  (let [bond-used?        (some :fee/bond-amount rows)
        bonds-available?  (<sub [::fee-bond/bonds (:db/id fsm-target)])
        can-bond?         #(and bonds-available?
                                (signal-permitted? fsm-target
                                                   [:process.signal/use-bond-for-fee {:bill-id bill-id
                                                                                      :fee-id  (:db/id %)}]))
        can-cancel-bond?  #(signal-permitted? fsm-target
                                              [:process.signal/cancel-bond-use-for-fee {:bill-id bill-id
                                                                                        :fee-id  (:db/id %)}])
        show-bond-button? (some #(or (can-cancel-bond? %)
                                     (and bonds-available? (can-bond? %)))
                                rows)
        show-wo?          (some (comp (complement str/blank?) :fee/work-order-number) rows)
        large-table?      (or bond-used? show-bond-button? show-wo?)
        table-bp          (if large-table? :xl :lg)
        lang              (<sub [:language])]
    #_{:clj-kondo/ignore [:deprecated-var]}
    [table/responsive-table {:tr                    tr
                             :class                 "mb-4"
                             :layout                :tight
                             :row-class-override    "lg:items-center px-4 py-2"
                             :table-mode-breakpoint table-bp}
     [{:title (tr [:fees/item])
       :width "250px"}
      {:title (tr [:fees/notes])}
      (when show-wo?
        {:title (tr [:fee/work-order-number])
         :width "200px"})
      (when show-bond-button?
        {:title ""
         :width "200px"})
      {:title (tr [:fees/quantity])
       :width "100px"}
      {:title (tr [:fees/unit])
       :width "80px"}
      {:title (tr [:fees/unit-price])
       :width "100px"
       :class "lg:text-right"}
      (when bond-used?
        {:title (tr [:fees/item-bond-total])
         :width "120px"
         :class "lg:text-right"})
      {:title (tr [:fees/waived])
       :width "80px"
       :class "lg:text-right"}
      {:title (tr [:fees/item-total])
       :width "120px"
       :class "lg:text-right"}]
     (->> rows
          (group-bill-rows lang tr)
          (mapcat (fn [{:keys [title rows subtotal]}]
                    (->> rows
                         (mapv (fn [{:db/keys [id] :as row}]
                                 {:key         (str "row-" (or id row))
                                  :opts        {:class "py-2"}
                                  :sub-header? (vector? row)
                                  :cols        (render-bill-cols (util/mmap
                                                                   fsm-target
                                                                   id
                                                                   bill-id
                                                                   row
                                                                   tr
                                                                   lang
                                                                   currency
                                                                   footnotes
                                                                   table-bp
                                                                   bond-used?
                                                                   can-cancel-bond?
                                                                   show-wo?
                                                                   show-bond-button?
                                                                   can-bond?
                                                                   tz))}))
                         (title-and-subtotal (util/mmap tr currency title subtotal))))))]))


(defn- render-bill-totals [{:keys [draft? paid? reversed? currency tr rows bill]}]
  (let [bill-id          (:db/id bill)
        total            (<sub [::bill-total bill-id])
        discounts        (<sub [::bill-discounts bill-id])
        last-payment     (fee-model/last-payment bill)
        cheque-number    (:payment/cheque-number last-payment)
        total-due        (+ total discounts)
        amount-paid      (:payment/amount last-payment)
        negative?        (< total-due 0)
        minimum-amounts? (->> rows
                              (filter is-minimum-amount?)
                              seq)]
    [:<>
     [:div.ml-auto.px-4.uppercase.text-right.pb-4
      {:style {:max-width    "500px"
               :margin-right (if draft? "42px" "0")}}
      [:div.flex.pb-2.mb-2.justify-end.border-b.border-gray-700.border-solid
       [:div.mr-2 (tr [:fees/bill-total])]
       [:div.md:w-48.font-mono.pr-2
        {:class (when negative? "text-red-700")}
        (futil/money-string total currency)]]
      [:div.flex.mb-2.justify-end
       [:div.mr-2.font-bold.text-right (cond
                                         (fee-model/refund-bill? bill) (tr [:fees/total-refunded])
                                         paid?                         (tr [:fees/total-paid])
                                         :else                         (tr [:fees/total-due]))]
       [:div.md:w-48.font-mono.font-bold.pr-2
        {:class (when negative? "text-red-700")}
        (futil/money-string (cond
                              (fee-model/refund-bill? bill) total
                              paid?                         amount-paid
                              :else                         total-due) currency)]]
      (when (and (or reversed? paid?) cheque-number)
        [:div.flex.mb-2.justify-end
         [:div.mr-2.text-right (str cheque-number " / " (tr [:payment/cheque]))]
         [:div.md:w-48.font-mono.pr-2
          (futil/money-string amount-paid currency)]])]
     (when minimum-amounts?
       [:div.px-4.text-right
        minimum-fee-indicator
        [:span.text-gray51 (tr [:fees/minimum-amount-info])]])]))

(defn- invoice-number [tr bill fsm-target bill-id]
  [:div.flex.items-center.text-left
   [:div.mr-2
    (tr [:fees/invoice-number])]
   [:div.w-40
    [ui/text-input {:id            (str "invoice-id-" bill-id)
                    :label-text    (tr [:fees/invoice-id])
                    :hide-label    true
                    :light         true
                    :default-value (fee-model/bill-invoice-number bill)
                    :disabled      (not (signal-permitted? fsm-target
                                                           [:process.signal/update-bill-invoice-number]))
                    :on-blur       #(when (futil/value-changed? %)
                                      (>evt [::update-bill-invoice-number
                                             fsm-target
                                             bill-id
                                             (-> % futil/tval)]))}]]])

(defn- invoice-payer
  [tr lang bill]
  (when-let [payer (:bill/payer bill)]
    [:div.flex.flex-1.mr-3.items-center
     (str (tr [:role/payer]) ": " (party-model/full-name-with-roles lang payer))]))

(defn- invoice-payer-select
  [tr bill fsm-target bill-id]
  (let [lang       (<sub [:language])
        authority? (contains? (:granted-permissions fsm-target) :permission.application/review-payments)
        parties    (if authority?
                     (party-model/active-parties fsm-target)
                     (->> (<sub [::party-subs/parties-for-current-user fsm-target])
                          (concat (when-let [current-payer (:bill/payer bill)]
                                    [current-payer]))
                          distinct))
        html-id    (str "payer-party-" bill-id)]
    (if (and (:bill/payer bill)
             (= 1 (count parties)))
      [invoice-payer tr lang bill]
      [:div.flex
       [:label.flex-initial.my-auto.mr-2.required-field
        {:for html-id}
        (tr [:role/payer])]
       (if (<sub [::ws-subs/loading-config?])
         [:div.w-48 (cc/select-skeleton {:hide-label true})]
         [ui-select/select
          {:id              html-id
           :external-label? true
           :required        (<sub [::payer-required?])
           :class-name      "full-width-select"
           :allow-empty?    false
           :val->str        #(party-model/full-name-with-roles lang %)
           :value           (:bill/payer bill)
           :tr              tr
           :on-change       (fn [{:db/keys [id]}]
                              (>evt [::update-bill-payer
                                     fsm-target
                                     bill-id
                                     id]))
           :values          parties}])])))

(defn- invoice-due-date-picker
  [{:keys [fsm-target tr bill bill-id editable? due-date-required?]}]
  (when (or editable? (some? (:bill/due-date-ts bill)))
    [:div.flex.items-center
     [:div.mr-2
      {:class (when (and editable? due-date-required?) "required-field")}
      (if editable?
        (tr [:fees/due-date])
        (str (tr [:fees/due-date]) ": "))]
     (if editable?
       [ui/date-picker (cond-> {:id         "bill-due-date-picker"
                                :label-text "Due date"
                                :tr         tr
                                :hide-label true
                                :required?  due-date-required?
                                :value      (:bill/due-date-ts bill)
                                :callback   (fn [d]
                                              (when-not (= d (:bill/due-date-ts bill))
                                                (let [new-date (-> (date-util/start-of-day d)
                                                                   (date-util/in-tz (<sub [::ws-subs/group-time-zone])))]
                                                  (>evt [::update-bill-due-date fsm-target bill-id new-date]))))}
                         (not (ff/enabled? :feature-flag.id/update-bill-due-date-to-past))
                         (merge {:min-date (:entity/created bill)}))]
       [:div.self-center ((<sub [::date-util/date-fn]) (:bill/due-date-ts bill))])]))


(defn- draft-footer-controls [tr bill-id fsm-target confirm-disabled? currency]
  [:<>
   (let [reset-signal [:process.signal/reset-bill {:bill-id bill-id}]]
     (when (<sub [::process/signal-permitted? fsm-target reset-signal])
       [ui/modal-confirmation-button
        {:tr                  tr
         :id                  "reset-bill"
         :trigger-button-text (tr [:fees/reset-bill])
         :trigger-button-opts {:kind             :ghost
                               :size             :small
                               :icon             i/refresh
                               :icon-position    :right
                               :icon-description (tr [:reset])}
         :modal-title         (tr [:fees/reset-bill])
         :modal-class         "text-left"
         :on-yes-click        #(>evt [::process/signal
                                      fsm-target
                                      reset-signal
                                      {:notify {:success :fees/bill-reset}}])}
        (tr [:fees/reset-bill-information])]))
   [ui/modal-confirmation-button
    {:tr                  tr
     :id                  "remove-bill"
     :trigger-button-text (tr [:fees/remove-bill])
     :trigger-button-opts {:kind             :tertiary
                           :size             :small
                           :icon             i/trash
                           :icon-position    :right
                           :icon-description (tr [:delete])}
     :modal-title         (tr [:fees/remove-bill])
     :modal-class         "text-left"
     :on-yes-click        #(>evt [::process/signal
                                  fsm-target
                                  [:process.signal/remove-bill {:bill-id bill-id}]
                                  {:notify {:success :fees/bill-removed}}])}
    (tr [:fees/remove-bill-information])]
   (when-not (<sub [::process/signal-forbidden? fsm-target [:process.signal/publish-bill {:bill-id bill-id}]])
     [fee-bond/bill-confirm-modal
      {:tr                tr
       :id                (str "confirm-bill-" bill-id)
       :confirm-disabled? confirm-disabled?
       :phase             fsm-target
       :bill-id           bill-id
       :currency          currency}])])

(defn bill-pdf-link
  [{:keys [tr] {:bill/keys [bill-pdf-document bill-pdf-document-generating?] :as bill} :bill}]
  (when (and (<sub [::ws-subs/has-feature? :generate-bill-pdf?])
             (or bill-pdf-document
                 bill-pdf-document-generating?))
    [:div
     (if bill-pdf-document
       (cc/button {:size    :small
                   :kind    :tertiary
                   :href    (file-download/download-href :fee/download-bill-pdf
                                                         {:bill-id (:db/id bill)})
                   :data-cy "download-bill"}
                  (tr [:bill/download]))
       [:div (cc/inline-loading {:description (tr [:bill/generating-invoice-document])})])]))

(defn- fmt-escrow-account [account lang]
  (let [owner      (:escrow-account/owner account)
        owner-name (case (:escrow-account/owner-type account)
                     :escrow-account.owner-type/business
                     (business-model/business-name owner lang)

                     (user-model/full-name owner))
        account-id (escrow-model/account-number account)
        balance    (:escrow-account/balance account)
        currency   (-> account :entity/parent :group/currency)
        balance    (futil/money-string-whole-units balance currency)]
    (str owner-name " " account-id " (" balance ")")))

(defn escrow-modal [fsm-target bill-id total should-also-submit? escrow-accounts currency tr]
  (let [lang               (<sub [:language])
        escrow-accounts    (sort-by #(fmt-escrow-account % lang)
                                    escrow-accounts)
        validity->accounts (group-by #(<sub [::valid-escrow-account?
                                             fsm-target
                                             bill-id
                                             (:db/id %)])
                                     escrow-accounts)
        selected-account   (or (<sub [::selected-escrow-account])
                               (first (get validity->accounts true))
                               (first escrow-accounts))
        invalid-accounts   (set (get validity->accounts false))
        no-valid-account?  (empty? (get validity->accounts true))
        cancel-fn          #(>evt [::close-escrow-modal])
        label              (tr [(if should-also-submit?
                                  :payment/pay-with-escrow-submit
                                  :payment/pay-with-escrow)])
        payment-pending?   (= bill-id (<sub [::pending-payment]))]
    [ui-modal/composed-input-modal
     {:open                   (<sub [::escrow-modal-open? bill-id])
      :selector-primary-focus "#select-escrow-account"
      :on-close               cancel-fn}
     (cc/modal-header {:close-modal cancel-fn
                       :title       label})
     (cc/modal-body {}
                    [:div.w-full.p-8.flex.flex-col
                     [:div.mb-6
                      [:span.mr-2 (tr [:fees/bill-total])]
                      [:span.font-bold (futil/money-string total currency)]]
                     [ui-select/select
                      {:id              "select-escrow-account"
                       :label-text      (tr [:payment/select-escrow-account])
                       :class-name      "full-width-select"
                       :values          escrow-accounts
                       :required        true
                       :allow-empty?    false
                       :value           selected-account
                       :val->str        #(fmt-escrow-account % lang)
                       :disabled-values invalid-accounts
                       :on-change       #(>evt [::set-selected-escrow-account %])}]
                     (when no-valid-account?
                       [ui/warning
                        ;; Without w-full text is split into multiple rows needlessly
                        {:content-class "w-full"}
                        (tr [:payment/insufficient-escrow-funds])])])
     (cc/modal-footer {}
                      (cc/button
                        {:kind     :secondary
                         :on-click cancel-fn}
                        (tr [:cancel]))
                      (cc/button
                        {:kind     :primary
                         :disabled (or no-valid-account?
                                       payment-pending?)
                         :on-click #(let [account-id (:db/id selected-account)]
                                      (if should-also-submit?
                                        (>evt [::partial-submit-and-pay-with-escrow fsm-target bill-id account-id])
                                        (>evt [::pay-with-escrow fsm-target bill-id account-id])))}
                        label))]))

(defn- card-types [tr online-payment-method]
  (when-some [card-types (some-> online-payment-method
                                 :payment-method/accepted-card-types
                                 sort)]
    [:div.flex.flex-wrap.items-center.gap-2
     [:div.text-xs (tr [:payment/available-online-methods])]
     (for-frag [card card-types]
       [:div
        [:img {:src   (str "/asset/img/" (name card) ".svg")
               :style {:height "18px"}
               :title (tr [card])
               :alt   (tr [card])}]])]))

(defn- pay-in-person-button [should-also-submit? fsm-target tr]
  (when should-also-submit?
    ;; Just does the normal partial submit, which makes the workspace visible to authorities
    (cc/button {:size     :small
                :kind     :tertiary
                :data-cy  "pay-in-person"
                :on-click #(>evt [::process/signal fsm-target [:process.signal/partial-submit]])}
               (tr [:payment/pay-in-person]))))

(defn- online-limit-exceeded [total-over-online-limit? online-limit currency tr]
  (when total-over-online-limit?
    [:div.flex.justify-end.my-2.text-xs.text-gray-800
     (tr [:fees/online-limit-exceeded]
         [(futil/money-string online-limit currency)])]))

(defn- unpaid-footer-controls [{:keys [tr authority-controls? fsm-target bill card-payment-method total currency
                                       escrow-accounts]}]
  (let [bill-id                    (:db/id bill)
        state                      (:domain/state bill)
        bill-cancel-reason         (<sub [::bill-cancel-reason bill-id])
        online-limit               (:payment-method/max-amount card-payment-method)
        total-over-online-limit?   (and (some? online-limit) (> total online-limit))
        ;; if Paymentus has use-municipal-case-id enabled and group has integration enabled -> wait until backend sync ready
        waiting-for-backend?       (and (<sub [::ws-subs/has-feature? :group.feature/backend-system-integration])
                                        (<sub [::ws-subs/payment-use-municipal-case-id?])
                                        (not (-> fsm-target :entity/parent :workspace/external-id)))
        should-also-submit?        (and (contains? #{:bill.phase/deposit :bill.phase/upfront} (:bill/phase bill))
                                        (signal-permitted? fsm-target [:process.signal/partial-submit]))
        pending-payment            (<sub [::pending-payment])
        current-bill-pmnt-pending? (= bill-id pending-payment)
        pay-buttons-disabled?      (or (= 0 total)
                                       waiting-for-backend?
                                       (some? pending-payment))
        [pay-online-label
         pay-online-event
         pay-with-escrow-label]    (if should-also-submit?
                                     [:payment/pay-online-submit
                                      [::partial-submit-and-initiate-online-payment fsm-target bill-id]
                                      :payment/pay-with-escrow-submit]
                                     [:payment/pay-online
                                      [::initiate-online-payment bill-id]
                                      :payment/pay-with-escrow])]
    [:<>
     (when authority-controls?
       [:div.flex.flex-wrap.items-center.justify-end
        [:div.my-1
         [ui/modal-confirmation-button
          {:tr                  tr
           :id                  "cancel-bill"
           :trigger-button-text (tr [:fees/cancel-bill])
           :trigger-button-opts {:kind             :tertiary
                                 :size             :small
                                 :icon             i/undo
                                 :icon-position    :right
                                 :icon-description (tr [:delete])
                                 :disabled         (not (signal-permitted? fsm-target
                                                                           [:process.signal/cancel-bill {:bill-id bill-id}]))}
           :modal-title         (tr [:fees/cancel-bill])
           :modal-class         "text-left"
           :yes-disabled?       (str/blank? bill-cancel-reason)
           :on-yes-click        #(>evt [::process/signal
                                        fsm-target
                                        [:process.signal/cancel-bill {:bill-id bill-id
                                                                      :reason  bill-cancel-reason}]
                                        {:notify {:success :fees/bill-canceled}}])}
          [:<>
           (tr [:fees/cancel-bill-information])
           [ui/text-area {:id          "cancel-reason"
                          :class       "mt-4"
                          :label-text  (tr [:fees/cancel-bill-reason])
                          :placeholder (tr [:fees/cancel-bill-placeholder])
                          :on-change   (fn [e] (>evt [::set-bill-cancel-reason bill-id (-> e futil/tval)]))}]]]]
        (when-not (<sub [::ws-subs/loading-config?])
          [:<>
           [set-paid-modal/set-paid-button fsm-target bill-id]
           [set-paid-modal/set-unpaid-button fsm-target bill-id]])
        (when (= state :bill.state/pending-confirmation)
          [:div.ml-2
           (cc/inline-loading {:description (tr [state])})])])
     (when (<sub [::escrow-modal-open? bill-id])
       [escrow-modal fsm-target bill-id total should-also-submit? escrow-accounts currency tr])
     (cond
       (<sub [::ws-subs/loading-config?])
       [cc/button-skeleton]

       (and card-payment-method escrow-accounts)
       [:div
        [:div.flex.flex-wrap.items-center.justify-end.gap-3
         [card-types tr card-payment-method]
         (if current-bill-pmnt-pending?
           [:div (cc/inline-loading {:description (tr [:loading])})]
           (cc/dropdown
             {:id            (str "pay-bill-" bill-id)
              :label         ""
              :title-text    (tr [:payment/pay])
              :hide-label    true
              :class-name    "select-action select-wide-menu"
              :items         (concat
                               [{:label    (tr [pay-online-label])
                                 :value    #(>evt pay-online-event)
                                 :disabled total-over-online-limit?}
                                {:label (tr [pay-with-escrow-label])
                                 :value #(>evt [::open-escrow-modal bill-id])}]
                               (when should-also-submit?
                                 [{:label (tr [:payment/pay-in-person])
                                   :value #(>evt [::process/signal
                                                  fsm-target
                                                  [:process.signal/partial-submit]])}]))
              :selected-item {:label (tr [:payment/pay])}
              :disabled      pay-buttons-disabled?
              :data-cy       "pay-online-or-with-escrow"
              :on-change     #(let [action (futil/oget % :selectedItem :value)]
                                (action))}))]
        [online-limit-exceeded total-over-online-limit? online-limit currency tr]]

       card-payment-method
       [:div
        [:div.flex.flex-wrap.items-center.justify-end.gap-3
         [card-types tr card-payment-method]
         (if current-bill-pmnt-pending?
           [:div (cc/inline-loading {:description (tr [:payment/loading-payment-page])})]
           [:<>
            [pay-in-person-button should-also-submit? fsm-target tr]
            (cc/button {:size             :small
                        :icon-position    :right
                        :icon             i/check
                        :icon-description ""
                        :disabled         (or pay-buttons-disabled?
                                              total-over-online-limit?)
                        :data-cy          "pay-online"
                        :on-click         #(>evt pay-online-event)}
                       (tr [pay-online-label]))])]
        [online-limit-exceeded total-over-online-limit? online-limit currency tr]]

       escrow-accounts
       (if current-bill-pmnt-pending?
         [:div (cc/inline-loading {:description (tr [:loading])})]
         (cc/button {:size             :small
                     :icon-position    :right
                     :icon             i/check
                     :icon-description ""
                     :disabled         pay-buttons-disabled?
                     :data-cy          "pay-with-escrow"
                     :on-click         #(>evt [::open-escrow-modal bill-id])}
                    (tr [pay-with-escrow-label]))))]))

(defn reported-payment-date [{:keys [tr bill]}]
  (let [latest-payment (fee-model/last-payment bill)]
    (when (and latest-payment
               (= (-> latest-payment :payment/payment-method :payment-method/type)
                  :payment-method/cash)
               ;; Old payments won't have a reported date
               (:payment/reported-payment-date latest-payment))
      [:span.mx-2 (str (tr [:payment/reported-payment-date]) ": "
                       ((<sub [::date-util/date-fn]) (:payment/reported-payment-date latest-payment)))])))

(defn- actual-payment-payer-name [{:keys [lang tr bill]}]
  (when-some [payer (-> bill
                        :entity/_parent
                        (p/find-first #(-> % :entity/type (= :entity.type/payment)))
                        :payment/payer)]
    [:span.mx-2 (str (tr [:role/payer]) ": " (party-model/corporation-and-full-name-or-full-name payer lang))]))

(defn receipt-download-button [{:keys [tr bill]}]
  (cond
    (:bill/receipt-document bill)
    (cc/button {:size    :small
                :kind    :tertiary
                :href    (file-download/download-href :fee/download-receipt
                                                      {:bill-id (:db/id bill)})
                :data-cy "download-receipt"}
               (tr [:receipt/download]))

    (:bill/receipt-document-generating? bill)
    [:div (cc/inline-loading {:description (tr [:receipt/generating-receipt-document])})]))

(defn- render-waiver-buttons
  "Renders the Accept Changes button with pending change count and cancel option"
  [{:keys [tr bill-id fsm-target]}]
  (let [pending-changes (<sub [::pending-fee-changes bill-id])
        change-count    (count pending-changes)]
    (when (pos? change-count)
      [:div.flex.items-center.gap-2
       [ui/button {:kind     :secondary
                   :size     :small
                   :on-click #(>evt [::clear-pending-changes bill-id])}
        (tr [:fees/revert-waivers])]
       [ui/button {:kind     :primary
                   :size     :small
                   :on-click #(>evt [::apply-fee-changes fsm-target bill-id])}
        (str (tr [:fees/confirm-waivers]) " (" change-count ")")]])))

(defn invoice-trail-toggle-button [bill-id expanded? tr]
  (ui/button {:size     :small
              :icon     i/plus
              :kind     :secondary
              :on-click #(>evt [::bill-events/toggle-invoice-trail bill-id])}
             (if expanded?
               (tr [:bill/hide-invoice-trail])
               (tr [:bill/show-invoice-trail]))))

(defn close-reversal-modal [] (>evt [::bill-events/close-reversal-modal]))

(defn reversal-modal [{:keys [tr bill]}]
  (let [bill-id          (:db/id bill)
        open?            (<sub [::bill-subs/reversal-modal-open? bill-id])
        reversal-methods (<sub [::bill-subs/available-reversal-methods bill])
        selected-method  (<sub [::bill-subs/selected-reversal-method])
        reason           (<sub [::bill-subs/reversal-reason])
        submitting?      (<sub [::bill-subs/reversal-submitting?])
        can-submit?      (<sub [::bill-subs/can-submit-reversal?])]

    [ui-modal/composed-input-modal
     {:open                   open?
      :selector-primary-focus "#reversal-method-select"
      :on-close               close-reversal-modal}
     (cc/modal-header {:close-modal close-reversal-modal
                       :title       (tr [:bill/reverse-payment])})
     (cc/modal-body {}
                    [:div.w-full.flex.flex-col.gap-2
                     [:div.flex.items-center.text-left.pb-4
                      [:div.mr-2
                       (tr [:fees/invoice-number])]
                      [:div.w-40
                       [:span (fee-model/bill-invoice-number bill)]]]

                     ;; Reversal method selection (NSF/VOID only)
                     [ui-select/select
                      {:id           "reversal-method-select"
                       :label-text   (tr [:bill/reversal-method])
                       :class-name   "full-width-select mb-4"
                       :values       reversal-methods
                       :required     true
                       :allow-empty? false
                       :value        selected-method
                       :val->str     :label
                       :on-change    #(>evt [::bill-events/set-selected-reversal-method %])}]

                     ;; Method description
                     (when-let [description (when selected-method
                                              (:description selected-method))]
                       [:div.mb-4.p-3.bg-gray-100.rounded
                        [:p.text-sm.text-gray-700 description]])

                     [ui/text-area
                      {:id          "reversal-reason"
                       :label-text  (tr [:bill/reversal-reason])
                       :placeholder (tr [:bill/reversal-reason-placeholder])
                       :required    true
                       :value       reason
                       :on-change   #(>evt [::bill-events/set-reversal-reason (futil/tval %)])}]])

     (cc/modal-footer {}
                      (cc/button
                        {:kind     :secondary
                         :disabled submitting?
                         :on-click close-reversal-modal}
                        (tr [:cancel]))
                      (ui/button
                        {:kind     :primary
                         :disabled (or (not can-submit?) submitting?)
                         :on-click #(>evt [::bill-events/submit-reversal bill-id])}
                        (if submitting?
                          (tr [:processing])
                          (tr [:bill/create-reversal-draft]))))]))

(defn reverse-payment-button [{:keys [tr bill workspace-id]}]
  (let [bill-id     (:db/id bill)
        schedule-id (get-in bill [:bill/schedule :db/id])]
    [:<>
     (cc/button {:size     :small
                 :kind     :tertiary
                 :on-click #(>evt [::bill-events/open-reversal-modal workspace-id bill-id schedule-id])}
                (tr [:bill/reverse-payment]))
     [reversal-modal {:bill bill
                      :tr   tr}]]))

(defn- render-bill-footer
  [{:keys [fsm-target bill tr currency escrow-accounts penalty?]}]
  (let [bill-id                   (-> bill :db/id)
        total                     (<sub [::bill-total bill-id])
        lang                      (<sub [:language])
        state                     (:domain/state bill)
        auth-group-id             (-> fsm-target :entity/parent :domain/authority-group :db/id)
        cancel-reason             (-> bill :bill/cancel-reason)
        authority-controls?       (or (signal-permitted? fsm-target [:process.signal/cancel-bill {:bill-id bill-id}])
                                      (signal-permitted? fsm-target [:process.signal/set-paid {:bill-id bill-id}])
                                      (signal-permitted? fsm-target [:process.signal/set-unpaid {:bill-id bill-id}]))
        let-payer-pay-bill?       (ff/enabled? :feature-flag.id/let-assigned-payer-pay-the-bill)
        ws-child                  (if let-payer-pay-bill? bill fsm-target)
        online-or-escrow-payment? (and (not authority-controls?)
                                       ;; Make sure that we do not ever show the pay online button for authorities
                                       (-> fsm-target
                                           :granted-permissions
                                           (contains? :permission.application/review-payments)
                                           not)
                                       (permissions/ws-child-has-permission? :permission.invoice/write ws-child)
                                       (not (:bill/legacy-bill? bill))
                                       (not (du/in-state? :state.application/rejected fsm-target)))
        refund-bill?              (fee-model/refund-bill? bill)
        refund-reversal-draft?    (and (fee-model/draft-reversal-bill? bill)
                                       refund-bill?)
        update-due-date           (ff/enabled? :feature-flag.id/update-bill-due-date)
        due-date-required?        (and update-due-date
                                       penalty?
                                       (not refund-reversal-draft?))
        confirm-disabled?         (or (-> bill
                                          :bill/fees
                                          empty?)
                                      (and due-date-required? (nil? (-> bill :bill/due-date-ts)))
                                      (not (signal-permitted? fsm-target
                                                              [:process.signal/publish-bill {:bill-id bill-id}])))
        card-payment-method       (when online-or-escrow-payment?
                                    (<sub [::fee-subs/online-payment-method auth-group-id]))
        escrow-accounts           (when (and online-or-escrow-payment?
                                             (<sub [::show-escrow-option? fsm-target bill-id]))
                                    escrow-accounts)]
    [:div.bg-ui-blue-10.mt-4.px-4.py-3.flex.items-center.flex-wrap.justify-between.gap-3
     [:div.flex.flex-wrap.items-center.gap-3
      [invoice-number tr bill fsm-target bill-id]
      (cond
        (signal-permitted? fsm-target [:process.signal/update-bill-payer {:bill-id bill-id}])
        [invoice-payer-select tr bill fsm-target bill-id]

        refund-bill?
        [:<> [invoice-payer tr lang bill]]

        (#{:bill.state/paid :bill.state/reversed} state)
        [:<>
         [actual-payment-payer-name (util/mmap lang tr bill)]
         [reported-payment-date (util/mmap tr bill)]]

        (= state :bill.state/unpaid)
        ;; Apparently we show different payer information depending on if the bill is paid or unpaid
        [invoice-payer tr lang bill])
      (when (and update-due-date (not refund-reversal-draft?))
        (let [editable? (signal-permitted? fsm-target [:process.signal/update-bill-due-date {:bill-id bill-id}])]
          (when (or editable? (#{:bill.state/paid :bill.state/unpaid} state))
            [invoice-due-date-picker (util/mmap {:editable? editable?}
                                                fsm-target tr bill bill-id due-date-required?)])))]

     [:div.flex.flex-grow.flex-wrap.items-center.justify-end.gap-3
      [render-waiver-buttons (util/mmap tr bill-id fsm-target)]
      [bill-pdf-link (util/mmap tr bill)]
      (case state
        :bill.state/canceled
        [:div
         [:span.font-bold.mr-2.align-bottom (str (tr [:fees/cancel-bill-reason]) ":")]
         [:span.align-text-bottom cancel-reason]]

        :bill.state/paid
        [:<>
         [receipt-download-button (util/mmap tr bill)]
         (when (and (signal-permitted? fsm-target [:process.signal/reverse-bill {:bill-id bill-id}])
                    (seq (<sub [::bill-subs/available-reversal-methods bill])))
           [reverse-payment-button (util/mmap {:workspace-id (-> fsm-target :entity/parent :workspace/id)}
                                              tr bill)])]

        :bill.state/reversed
        [receipt-download-button (util/mmap tr bill)]

        :bill.state/draft
        [draft-footer-controls tr bill-id fsm-target confirm-disabled? currency]

        :bill.state/unpaid
        [unpaid-footer-controls (util/mmap tr authority-controls? fsm-target bill card-payment-method total currency
                                           escrow-accounts)]

        :bill.state/pending-confirmation
        (if authority-controls?
          [unpaid-footer-controls (util/mmap tr authority-controls? fsm-target bill card-payment-method total currency
                                             escrow-accounts)]
          [:div (cc/inline-loading {:description (tr [state])})]))]]))


(def header-extra :div.flex-initial.w-70.mt-2.mx-2.text-right>div.font-normal)

(defn render-bill-payment-date [{:keys [latest-payment tr]}]
  [header-extra (str (tr [(if (= (-> latest-payment :payment/payment-method :payment-method/type)
                                 :payment-method/cash)
                            :marked-as-paid-on
                            :paid-on)])
                     " "
                     ((<sub [::date-util/date-time-fn])
                      (-> latest-payment :payment/paid-ts)))])

(defn- render-bill-header
  [{:keys [bill tr fsm-target]}]
  (let [{phase   :bill/phase state    :domain/state
         bill-id :db/id      schedule :bill/schedule} bill
        fee-schedules                                 (<sub [::fee-schedules])
        lang                                          (<sub [:language])
        latest-payment                                (->> bill
                                                           fee-model/payments
                                                           (sort-by :payment/paid-ts #(compare %2 %1))
                                                           first)
        invoice-number                                (fee-model/bill-invoice-number bill)
        draft?                                        (= :bill.state/draft state)
        paid?                                         (= :bill.state/paid state)
        reversed?                                     (= :bill.state/reversed state)]
    [:div.p-4.pt-2.flex.w-full.justify-between.items-center.font-bold.flex-wrap
     {:class [(when (or reversed? paid?) "bg-gray-400")
              (when draft? "bill-draft-header")
              (when (#{:bill.state/unpaid :bill.state/pending-confirmation} state) "bg-ui-dark-gray")
              (when (= :bill.state/canceled state) "bg-gray-100")]}
     [:div.flex-1.mt-2 (tr [phase])]
     (when (and (not draft?)
                invoice-number)
       [header-extra (str (tr [:fees/invoice-number]) " " invoice-number)])
     (cond
       (and (or reversed? paid?) latest-payment)
       [render-bill-payment-date (util/mmap latest-payment tr)])
     [:div.bill-fee-schedule-select.mt-2
      (if-let [schedule-id (and
                             fsm-target
                             (signal-permitted? fsm-target [:process.signal/update-bill-fee-schedule {:bill-id bill-id}])
                             (:db/id schedule))]
        (cc/select
          {:id         "fees-schedule"
           :label-text "Fees schedule"
           :value      schedule-id
           :on-change  #(>evt [::update-bill-fee-schedule fsm-target (:db/id bill) (-> % .-target .-value futil/parse-int)])
           :hide-label true}
          (for-frag [schedule (sort-by :fee-schedule/valid-from fee-schedules)]
            (cc/select-item {:text  (fee-model/fee-schedule-title schedule lang)
                             :value (:db/id schedule)})))
        [:div.font-normal (fee-model/fee-schedule-title schedule lang)])]]))


(defn- add-new-fee-item [{:keys [tr fee-templates fsm-target id]}]
  (let [lang (<sub [:language])]
    [:div.px-4.mb-8
     [:div.mr-4
      (cc/select
        {:id         "fees-templates"
         :label-text "fees-templates"
         :hide-label true
         :value      ""
         :on-change  #(>evt [::add-fee fsm-target id (-> % futil/tval futil/parse-int)])}
        (cc/select-item {:key   "default"
                         :text  (tr [:fees/add-new-fee])
                         :value ""})
        (for-frag [fee-template fee-templates]
          (cc/select-item {:key   (str (:db/id fee-template))
                           :text  (fee-model/fee-template-title fee-template lang)
                           :value (:db/id fee-template)})))]]))

(defn- render-reversal-info-header
  "Display reversal information above bill header in invoice trail for reversed bills.
   Shows as: Reversed at <date> by <user> with <method> with reason: <reason>."
  [{:keys [bill tr]}]
  (let [{:bill/keys [reversed-at reversed-by
                     reversal-method reversal-reason]} bill
        lang                                           (<sub [:language])
        date-str                                       ((<sub [::date-util/date-time-fn]) reversed-at)
        user-name                                      (str (:user/first-name reversed-by) " " (:user/last-name reversed-by))
        method-title                                   (fee-model/reversal-method-title reversal-method lang)]
    [:div.py-4.bg-ui-white.border-b.border-gray-300.text-sm.flex.flex-col.gap-1
     [:span (tr [:bill/reverse-info-header] [date-str user-name method-title reversal-reason])]]))

(defn bill-chain [bill]
  (->> (iterate :bill/previous-bill bill)
       rest
       (take-while some?)))

(declare render-bill)

(defn render-invoice-trail-section [{:keys [bill fsm-target tr]}]
  (when-let [previous-bills (seq (bill-chain bill))]
    (let [bill-id   (:db/id bill)
          expanded? (<sub [::bill-events/invoice-trail-expanded? bill-id])]
      [:div.invoice-trail.bg-white
       [:div.p-2.bg-ui-blue-10
        [invoice-trail-toggle-button bill-id expanded? tr]]
       (when expanded?
         (for-frag [prev-bill previous-bills]
           [:div.my-2.mx-8.border.border-gray-300.rounded.bg-gray-100
            [render-reversal-info-header (util/mmap {:bill prev-bill} tr)]
            [render-bill (util/mmap {:bill prev-bill} fsm-target tr)]]))])))

(defn- render-bill [{:keys [fsm-target bill tr fee-templates escrow-accounts]}]
  (let [id        (-> bill :db/id)
        phase     (-> bill :bill/phase)
        rows      (-> bill :bill/fees)
        state     (-> bill :domain/state)
        draft?    (= :bill.state/draft state)
        paid?     (= :bill.state/paid state)
        reversed? (= :bill.state/reversed state)
        currency  (-> fsm-target :entity/parent :domain/authority-group :group/currency)
        footnotes (fee-model/collect-footnotes rows)
        tz        (-> fsm-target :entity/parent :domain/authority-group :group/tz)
        penalty?  (boolean (some :fee-template/allow-penalty? rows))]
    [:div.mb-4.workspace-bill
     {:class [(if paid?
                "border-gray-200 border-2 border-solid"
                "bg-gray-200")]}
     [render-bill-header (util/mmap bill tr fsm-target)]

     (if draft?
       [render-draft-bill-rows (util/mmap {:bill-id id} tr phase currency fsm-target rows footnotes)]
       [render-bill-rows (util/mmap {:bill-id id} tr currency rows fsm-target footnotes tz)])

     (when (signal-permitted? fsm-target [:process.signal/add-fee {:bill-id id}])
       [add-new-fee-item (util/mmap tr fee-templates fsm-target id phase)])

     [render-bill-totals (util/mmap tr currency draft? paid? reversed? rows bill)]

     (for-frag [[[date-reference reference-date] ident] footnotes]
       [:div.text-purple-800.ml-4 ident " "
        (tr [(keyword "fee.date-calculation.note" (name date-reference))]
            [(date-util/date-in-tz reference-date tz)])])

     [render-bill-footer (util/mmap tr bill fsm-target currency escrow-accounts penalty?)]

     (when (and (fee-model/reversal-bill? bill)
                (not reversed?)
                (ff/enabled? :feature-flag.id/bill-reverse))
       [render-invoice-trail-section (util/mmap bill fsm-target tr currency)])]))


(defn fees-and-payments [tr fsm-target]
  (let [bills           (<sub [::bills-visible-in-phase fsm-target])
        paid-bills      (filter (du/in-state-fn :bill.state/paid) bills)
        structure       {:drafts   (filter (du/in-state-fn :bill.state/draft) bills)
                         :unpaid   (filter (du/in-state-fn :bill.state/unpaid :bill.state/pending-confirmation) bills)
                         :paid     (remove fee-model/refund-bill? paid-bills)
                         :refunded (filter fee-model/refund-bill? paid-bills)
                         :canceled (filter (du/in-state-fn :bill.state/canceled) bills)}
        domain-group    (-> fsm-target
                            group-model/get-authority-group
                            :entity/parent)
        escrow-accounts (when (group-model/has-feature? domain-group :group.feature/escrow-accounts)
                          (<sub [::escrow-accounts fsm-target (:db/id domain-group)]))
        bill-props      (fn [bill id title]
                          (util/mmap
                            {:fee-templates (<sub [::fee-templates (-> bill :bill/schedule :db/id)])}
                            fsm-target bill title id tr escrow-accounts))
        signal-k        (if (eu/is-type? :entity.type/pre-consultation fsm-target)
                          :process.signal/add-pre-consultation-bill
                          :process.signal/add-bill)]
    [:<>
     [:div.fees-header.flex.flex-wrap.justify-between.items-center
      [:div.-mt-2
       (for-frag [[phase kind tr-key] [[:bill.phase/pre-consultation :primary :process.signal/add-pre-consultation-bill]
                                       [:bill.phase/permit :primary :fees/add-permit-bill]
                                       [:bill.phase/additional :tertiary :fees/add-additional-bill]]]
         (let [signal [signal-k {:phase phase}]]
           (when (signal-permitted? fsm-target signal)
             (cc/button {:kind             kind
                         :size             :small
                         :icon             i/plus
                         :icon-description ""
                         :class-name       "mr-4 mt-2"
                         :on-click         #(>evt [::process/signal fsm-target signal])}
                        (tr [tr-key])))))]]

     (for-frag [[key bills] structure
                :when (and (seq bills)
                           ;; FIXME: This check is necessary because the user may receive draft bills in
                           ;; apply-signal response. See WIN-1644.
                           (or (not= key :drafts)
                               (signal-permitted? fsm-target [:process.signal/publish-bill])
                               (signal-permitted? fsm-target [:process.signal/remove-bill])
                               (signal-permitted? fsm-target [:process.signal/cancel-bill])
                               (signal-permitted? fsm-target [:process.signal/update-bill-invoice-number])))]
       [:<>
        [:h2.mt-6.mb-2.text-base.font-bold.uppercase (str (tr [key]) " (" (count bills) ")")]

        [:> TransitionGroup {:component nil}
         (for-frag [bill bills
                    :let [title (tr [(keyword key)])
                          id    (-> bill :db/id)]]
           [:> CSSTransition {:key        id
                              :timeout    400
                              :in         true
                              :classNames "fade-right"}
            [render-bill (bill-props bill id title)]])]])]))


(defn format-instructions [instructions]
  (when-not (str/blank? instructions)
    (->> (str/split instructions #"\n")
         (mapcat (fn [line]
                   [(-> (str " " line)
                        (str/replace #"\s(www\.[^\s]+)" " https://$1")
                        (str/split #"\s")
                        (->> (map (fn [word]
                                    (if (re-matches #"(https?:[^\s]+)" word)
                                      [:a {:href   word
                                           :target "_blank"}
                                       word]
                                      word)))
                             (interpose " ")))
                    [:br]])))))


(defn payment-instructions
  "Renders the payment instructions while preserving newlines and links
  The same logic for emails can be found in `app.domain.fee.core/process-payment-instructions`
  If convenience fees are in use includes a notice about them."
  [tr phase]
  (let [auth-group                                        (-> phase :entity/parent :domain/authority-group)
        {:payment-method/keys [convenience-fee-percentage
                               convenience-fee-fixed]}    (<sub [::fee-subs/online-payment-method (:db/id auth-group)])
        convenience-fee                                   (cond
                                                            (and convenience-fee-percentage convenience-fee-fixed)
                                                            (str convenience-fee-percentage
                                                                 "% + "
                                                                 (futil/money-string-whole-units (parse-double convenience-fee-fixed)
                                                                                                 (:group/currency auth-group)))

                                                            convenience-fee-percentage
                                                            (str convenience-fee-percentage "%")

                                                            convenience-fee-fixed
                                                            (futil/money-string-whole-units (parse-double convenience-fee-fixed)
                                                                                            (:group/currency auth-group)))
        instructions                                      (-> phase :entity/parent :domain/authority-group :payment/instructions format-instructions)]

    (when (or instructions convenience-fee)
      (cond->> instructions
        convenience-fee (#(concat % [[:br] (tr [:fees/convenience-fee-added] [convenience-fee])]))
        :always (into [:div.my-6.text-gray68])))))


(defn section-state [{:keys [tr loading?]} fsm-target]
  (let [unpaid-total (<sub [::unpaid-total (:db/id fsm-target)])
        currency     (-> fsm-target :entity/parent :domain/authority-group :group/currency)]
    (util/mmap
      {:heading-extra [:div.flex
                       (when (> unpaid-total 0)
                         [:<>
                          [:span.mr-2 (tr [:fees/total-due])]
                          [:span.font-bold (futil/money-string unpaid-total currency)]])]}
      tr loading?)))


(defn fees-and-payments-section
  "The new fees and payments implementation"
  [{:keys [tr loading?]} fsm-target]
  (let [invoices-paid? (<sub [::invoices-paid? (:db/id fsm-target)])]
    (when (<sub [::fees-section-visible? fsm-target])
      [ws-section/section
       fsm-target
       :application-fees-and-payments
       (section-state (util/mmap tr loading?) fsm-target)
       :application/fees-and-payments
       invoices-paid?
       [:<>
        [payment-instructions tr fsm-target]
        [fees-and-payments tr fsm-target]]])))
